import { Form, Select } from "antd"
import debounce from "lodash/debounce"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLoaderData } from "react-router"

import { searchCompanies } from "~/api/pre-meeting"
import { OSLoader } from "~/components/base-loader"
import useGlobalStore from "~/store/global"

const SearchCompany = ({
  formError,
  disabled = false
}: {
  formError: any
  disabled: boolean
}) => {
  const loaderData = useLoaderData<any>()
  const { t } = useTranslation("common")
  const clientDomain = loaderData?.sessionDetails?.clientDomain
  const defaultCompanies = useMemo(
    () =>
      clientDomain?.name
        ? [
            {
              name: clientDomain?.name,
              id: clientDomain.id,
              primary_domain: clientDomain?.domain
            }
          ]
        : [],
    [clientDomain]
  )
  const [fetchingCompanies, setFetchingCompanies] = useState(false)
  const [companies, setCompanies] = useState(() => defaultCompanies)
  const [loadingCompanyInfo] = useState(false)
  const tokenInfo = useGlobalStore((state) => state.tokenInfo)
  const filterOption = useCallback((inputText: string, option: any) => {
    return (
      option.label.toString().toLowerCase().includes(inputText.toLowerCase()) ||
      option.value.toString().toLowerCase().includes(inputText.toLowerCase())
    )
  }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchCompany = useCallback(
    debounce(async (inputValue: string) => {
      if (!inputValue) return
      setFetchingCompanies(true)
      setCompanies([])
      const response = await searchCompanies(tokenInfo, inputValue)
      if ("error" in response) {
        setFetchingCompanies(false)
        return
      }
      const json = await response.json()
      setFetchingCompanies(false)
      setCompanies(json.data.organizations)
    }, 500),
    [tokenInfo]
  )

  const companyOptions = companies?.map((item: any) => ({
    title: item.name,
    label: (
      <div className="p-1">
        <p className="text-13">{item.name}</p>
        <p className="text-xs">{item.primary_domain || item.website_url}</p>
      </div>
    ),
    value: JSON.stringify({
      id: item.id,
      name: item.name,
      primary_domain: item.primary_domain
    })
  }))

  return (
    <>
      <Form.Item
        label={
          <span className={formError ? "text-red-500" : ""}>
            {t("pre_meeting_brief.label_additional_filters")}
          </span>
        }
        name="pre_meeting_company"
        className="mb-0 flex-1"
      >
        <Select
          className={`custom-select-filter h-9 input-field rounded-[10px] w-1/4 ${
            disabled
              ? "!bg-gray-300 dark:!bg-dark-2"
              : "!bg-[whitesmoke] dark:!bg-dark-1"
          }`}
          placeholder={t("pre_meeting_brief.placeholder_search_by_account")}
          showSearch
          allowClear
          disabled={disabled}
          options={companyOptions}
          onSearch={debouncedFetchCompany}
          loading={loadingCompanyInfo}
          filterOption={filterOption}
          labelRender={(val: any) => {
            try {
              const parsed = JSON.parse(val.value)
              return parsed.name
            } catch {
              return val.title as string
            }
          }}
          notFoundContent={
            fetchingCompanies ? (
              <div className="flex items-center justify-center">
                <OSLoader />
              </div>
            ) : null
          }
        />
      </Form.Item>
    </>
  )
}

export default SearchCompany
